/**
 * Cliente Supabase Global con soporte para Auth0 Token
 *
 * IMPORTANTE: Este archivo está en proceso de migración.
 *
 * Patrón actual (Alternativa B - temporal):
 * - El cliente global usa un token que se actualiza desde SupabaseProvider
 * - Los servicios que importan este cliente obtendrán automáticamente el token
 *
 * Patrón objetivo (Alternativa A - final):
 * - Los servicios deben recibir el cliente como parámetro
 * - Usar: servicioService.metodo(client, params)
 * - El cliente viene de useSupabase() hook
 *
 * @see src/services/serviciosService.ts para ejemplo del patrón correcto
 * @see .claude/guides/migracion-rls-multi-tenant.md para plan de migración
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// ============================================================
// SISTEMA DE TOKEN DINÁMICO (Alternativa B - Temporal)
// ============================================================

/**
 * Token actual de Auth0 - actualizado por SupabaseProvider
 * @internal Solo usar desde setGlobalSupabaseToken y el fetch interceptor
 */
let currentAuth0Token: string | null = null

/**
 * Actualiza el token global de Auth0 para el cliente Supabase
 * Llamado automáticamente por SupabaseProvider cuando el usuario se autentica
 *
 * @param token - Token JWT de Auth0, o null si el usuario cerró sesión
 */
export function setGlobalSupabaseToken(token: string | null): void {
  currentAuth0Token = token
}

/**
 * Obtiene el token actual (solo para debugging)
 * @internal
 */
export function getGlobalSupabaseToken(): string | null {
  return currentAuth0Token
}

// ============================================================
// CLIENTE SUPABASE GLOBAL
// ============================================================

const SUPABASE_CLAIMS_NAMESPACE = 'https://supabase.io/jwt/claims'

/**
 * Tira si el JWT no trae un `role` dentro de `https://supabase.io/jwt/claims`.
 * El RLS de BrainHub solo mira ese claim, así que un token sin él recibe 200 con 0 filas.
 * Un token que no se puede decodificar también tira.
 * Lo usan los interceptores de fetch de los dos clientes antes de emitir la request.
 */
export function assertSupabaseRoleClaim(token: string): void {
  let role: unknown
  try {
    const segment = token.split('.')[1]
    const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')
    const bytes = Uint8Array.from(atob(padded), (c) => c.charCodeAt(0))
    const payload = JSON.parse(new TextDecoder().decode(bytes))
    role = payload?.[SUPABASE_CLAIMS_NAMESPACE]?.role
  } catch {
    throw new Error('Supabase: el token de Auth0 no es un JWT legible. No se emite la request.')
  }

  if (typeof role !== 'string' || role === '') {
    throw new Error(
      `Supabase: el token de Auth0 no trae el claim role en ${SUPABASE_CLAIMS_NAMESPACE}. ` +
        'Sin él el RLS no matchea y la consulta vuelve vacía. No se emite la request.',
    )
  }
}

/**
 * Interceptor de fetch del cliente global. Exportado para poder verificar el caso sin
 * token, que es el que importa: embebido en `createClient` no hay forma de ejercitarlo.
 *
 * Sin token NO se emite la request (ORR-251, mismo criterio que ORR-236 en
 * supabase-context.tsx). Mandarla igual la resolvía con la anon key, y como el RLS de
 * BrainHub solo verifica el claim `role`, el resultado era 200 con 0 filas —
 * indistinguible de "no hay datos". Ese fallo silencioso es el que se presentó como
 * "Acceso Denegado" en ORR-236. El throw es ruidoso a propósito: deja el problema a la
 * vista en vez de disfrazarlo de tabla vacía.
 *
 * Ningún camino legítimo necesita este cliente sin token: la ruta pública
 * `/contrato/:token` resuelve server-side (`netlify/functions/contrato-publico.ts`,
 * ORR-247) y no pasa por acá.
 */
export const fetchConTokenAuth0: typeof fetch = async (url, options = {}) => {
  if (!currentAuth0Token) {
    throw new Error(
      'Supabase: request sin token de Auth0. El cliente global solo opera autenticado ' +
        '(setGlobalSupabaseToken lo carga desde SupabaseProvider).',
    )
  }
  assertSupabaseRoleClaim(currentAuth0Token)

  const headers = new Headers(options.headers)
  headers.set('Authorization', `Bearer ${currentAuth0Token}`)

  return fetch(url, { ...options, headers })
}

/**
 * Cliente Supabase global con interceptor de token Auth0
 *
 * @deprecated Para nuevos servicios, usar el patrón de inyección:
 * ```typescript
 * export const miService = {
 *   async metodo(client: SupabaseClient<Database>, params: Params) {
 *     const { data, error } = await client.from('tabla').select()
 *     // ...
 *   }
 * }
 * ```
 */
export const supabase: SupabaseClient<Database> | null =
  supabaseUrl && supabaseAnonKey
    ? createClient<Database>(supabaseUrl, supabaseAnonKey, {
        // La auth real es Auth0 (inyectada vía fetch interceptor), no GoTrue.
        // storageKey propio: convive con el cliente del SupabaseProvider sin disparar
        // el warning "Multiple GoTrueClient instances" (auth-js indexa el contador de
        // instancias por storageKey). persistSession/autoRefreshToken off: no hay sesión
        // Supabase que persistir ni refrescar.
        auth: {
          persistSession: false,
          autoRefreshToken: false,
          storageKey: 'sb-brainhub-public',
        },
        global: { fetch: fetchConTokenAuth0 },
      })
    : null

// ============================================================
// HELPERS
// ============================================================

/**
 * Verifica si Supabase está configurado
 */
export function isSupabaseConfigured(): boolean {
  return supabase !== null
}

/**
 * Crea un cliente autenticado con un token específico
 * @deprecated Usar useSupabase() hook en componentes React
 */
export function createAuthenticatedClient(accessToken: string): SupabaseClient<Database> {
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Supabase no está configurado. Agrega VITE_SUPABASE_URL y VITE_SUPABASE_ANON_KEY al archivo .env')
  }

  return createClient<Database>(supabaseUrl, supabaseAnonKey, {
    global: {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    },
  })
}

// Re-exportar tipos útiles
export type { SupabaseClient } from '@supabase/supabase-js'
export type { Database } from '@/types/database'
